import Link from 'next/link';
import Pager from 'rc-pagination';
import * as React from 'react';
import { InjectedIntlProps, injectIntl } from 'react-intl';
import Merchant from '../../../domain/entities/Merchant';
import MerchantCategory from '../../../domain/entities/MerchantCategory';
import BookingPage, { Pagination } from '../../../domain/entities/BookingPage';
import { BreadCrumbsProps } from '../../../utils/breadcrumbs';
import Button, { ButtonSize } from '../../common/buttons/Button';
import Heading from '../../common/sections/Heading';
import BaseTemplate, { TemplateProps } from '../common/BaseTemplate';
import BookingPageCard from '../common/cards/BookingPageCard';
import MerchantProfile from '../show/MerchantProfile';
import css from './MerchantBookingServicesPage.module.scss';

export interface PageProps extends TemplateProps {
  public_id: string;
  merchant: Merchant;
  category: MerchantCategory;
  categories: MerchantCategory[];
  bookingPages: BookingPage[];
  pagination: Pagination;
}

type Props = PageProps & InjectedIntlProps & BreadCrumbsProps;

class MerchantBookingServicesPage extends React.Component<Props> {
  constructor(props: Props) {
    super(props);
    this.handlePageChange = this.handlePageChange.bind(this);
  }

  public handlePageChange(page: number) {
    location.href = `${location.pathname}?page=${page}#pageContent`;
  }

  public render() {
    const {
      categories,
      bookingPages,
      breadcrumbs,
      merchant,
      pagination: { totalSize, currentPage },
      intl,
    } = this.props;

    return (
      <div className={css.MerchantBookingServicesPage}>
        <BaseTemplate
          {...this.props}
          footerProps={{ categories, isMerchantStickyFooterExist: true }}
          merchant={merchant}
          breadcrumbs={breadcrumbs}
        >
          <div className={css.MerchantBookingServicesPage__Content}>
            <Heading className={css.MerchantBookingServicesPage__Heading}>
              <Link
                href={`/${merchant.publicId}/booking_pages#pageContent`}
                prefetch={false}
              >
                <a className={css.MerchantBookingServicesPage__Link}>
                  {intl.formatMessage({
                    id: 'merchants.bookingPages.index.title',
                  })}
                </a>
              </Link>
              {currentPage > 1 &&
                ` ${intl.formatMessage(
                  { id: 'merchants.bookingPages.index.page' },
                  { currentPage },
                )}`}
            </Heading>
            {bookingPages.length > 0 && (
              <div>
                {bookingPages.map((bookingPage, i) => (
                  <BookingPageCard
                    key={i}
                    className={css.MerchantBookingServicesPage__BookingPageCard}
                    title={bookingPage.name}
                    description={bookingPage.description.text || ''}
                    url={`/${merchant.publicId}/${bookingPage.publicId}#pageContent`}
                    thumbnailUrl={bookingPage.thumbnailUrl}
                    tags={bookingPage.tagNames}
                    lowestPrice={bookingPage.lowestPrice}
                  />
                ))}
              </div>
            )}
            <div className={css.MerchantBookingServicesPage__Pager}>
              <Pager
                onChange={this.handlePageChange}
                current={currentPage}
                total={totalSize}
                showTitle={false}
              />
            </div>
            <div
              className={
                css.MerchantBookingServicesPage__MerchantProfileSection
              }
            >
              <Heading className={css.MerchantBookingServicesPage__Heading}>
                {intl.formatMessage(
                  { id: 'merchants.details.storeData' },
                  { displayName: merchant.displayName },
                )}
              </Heading>
              <MerchantProfile merchant={merchant} />
              <div
                className={
                  css.MerchantBookingServicesPage__BackToTopButtonSection
                }
              >
                <Button
                  className={css.MerchantBookingServicesPage__BackToTopButton}
                  isGhost={true}
                  isFull={true}
                  href={`/${merchant.publicId}`}
                  buttonSize={ButtonSize.Medium}
                >
                  {intl.formatMessage(
                    {
                      id: 'merchants.details.backToMerchantTop',
                    },
                    { displayName: merchant.displayName },
                  )}
                </Button>
              </div>
            </div>
          </div>
        </BaseTemplate>
      </div>
    );
  }
}

export default injectIntl(MerchantBookingServicesPage);
